import { Link } from '@remix-run/react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  LucideBuilding,
  LucideExternalLink,
  LucideEyeOff,
  LucideImage,
} from 'lucide-react'
import React, { useMemo } from 'react'
import { aUser, useAtomValue } from '~/atoms'
import { Image, ListingPreviewBrokers } from '~/components'
import { Badge, Button, Checkbox, Tooltip } from '~/components/ui'
import { useEmbed } from '~/hooks'
import {
  getDom,
  getLandDetailPriceValues,
  getLatestTransactionListingBrokers,
  getSharedBrokerCompany,
  mergeTransactionBrokersWithListingBrokerDetails,
  type Company,
  type Listing,
  type MapListingProjection,
  type ModelCompany,
  type TransactionBroker,
  type User,
} from '~/models'
import {
  STATUSES,
  TYPES,
  cn,
  fmtAddress,
  fmtCurrencyAmount,
  fmtNumberAbbr,
  getPropertyTypeClassName,
} from '~/utils'

type CardBadge = { child: string | React.ReactNode; content?: string }

function SelectionCheckbox({
  checked,
  onCheckChanged,
}: {
  checked: boolean
  onCheckChanged: (value: boolean) => void
}) {
  // const [checkedListings, setCheckedListings] = useAtom(aCheckedListings)

  return (
    <Checkbox
      color=""
      className="relative z-40 h-8 w-8 border-background bg-background text-accent-foreground shadow-md ring-primary ring-offset-transparent hover:outline-none hover:ring-1 hover:ring-offset-1 data-[state=checked]:border-primary"
      // checked={checkedListings.includes(listing._id)}
      checked={checked}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onCheckChanged(!checked)
        // if (checkedListings.includes(listing._id)) {
        //   setCheckedListings(checkedListings.filter((id) => id !== listing._id))
        // } else {
        //   setCheckedListings([...checkedListings, listing._id])
        // }
      }}
    />
  )
}

function CardBadges({
  listing,
  user,
}: {
  listing: Listing | MapListingProjection
  user?: User | null
}) {
  if (!listing.filters) {
    return null
  }
  // let values: (string | React.ReactNode)[] = []
  const filters = listing.filters!
  const children: CardBadge[] = []
  const isLand = listing.propertyTypes[0] === 'Land'
  const meta = getFullListingMeta(listing)
  const hasNegotiablePrice =
    meta?.priceNegotiable || filters.priceLabelAbbr === 'Negotiable'

  if (isLand) {
    const landPriceValues = meta
      ? getLandDetailPriceValues(meta, listing.status)
      : []

    if (landPriceValues.length > 0) {
      landPriceValues.forEach(({ label, value }) => {
        children.push({
          child: value,
          content: `${label}: ${value}`,
        })
      })
    } else if (filters.priceLabelAbbr) {
      children.push({
        child: filters.priceLabelAbbr,
        content: filters.priceLabel,
      })
    }
  } else if (filters.priceLabelAbbr) {
    children.push({
      child: filters.priceLabelAbbr,
      content: filters.priceLabel,
    })
  }

  if (
    !hasNegotiablePrice &&
    !isLand &&
    filters.priceType !== 'sf' &&
    filters.priceType !== 'acre'
  ) {
    // Total
    if (filters.price && filters.buildingSf) {
      const pricePerSf = filters.price / filters.buildingSf
      if (pricePerSf >= 10) {
        children.push({
          child: `$${fmtCurrencyAmount(pricePerSf)}/SF`,
          content: `Price/SF: $${fmtCurrencyAmount(pricePerSf)}`,
        })
      }
    }
  }

  if (filters.capRate) {
    children.push({
      child: `${filters.capRate}%`,
      content: `Cap Rate: ${filters.capRate}%`,
    })
  }

  if (listing.type.toLowerCase().includes('lease')) {
    if (listing.status != 'leased') {
      if (filters.rateLabelAbbr) {
        children.push({
          child: filters.rateLabelAbbr,
          content: `Rate: ${filters.rateLabel}`,
        })
      }
      ////// TODO Remove rate here once all rate labels are generated
      else if (
        filters.minRate &&
        filters.maxRate &&
        filters.minRate !== filters.maxRate
      ) {
        children.push({
          child: `$${filters.minRate} - $${filters.maxRate} SF/year`,
          content: `Rate: $${filters.minRate} - $${filters.maxRate} SF/year`,
        })
      } else if (filters.minRate) {
        children.push({
          child: `$${filters.minRate} SF/year`,
          content: `Rate: $${filters.minRate} SF/year`,
        })
      }
      //////

      if (
        filters.minSf &&
        filters.maxSf &&
        fmtNumberAbbr(filters.minSf) !== fmtNumberAbbr(filters.maxSf)
      ) {
        children.push({
          child: `${fmtNumberAbbr(filters.minSf)} - ${fmtNumberAbbr(filters.maxSf)} SF`,
          content: `Available SF: ${filters.minSf.toLocaleString()} - ${filters.maxSf.toLocaleString()} SF`,
        })
      } else if (filters.minSf) {
        children.push({
          child: `${fmtNumberAbbr(filters.minSf)} SF`,
          content: `Available SF: ${filters.minSf.toLocaleString()} SF`,
        })
      }
      if (filters.spaces && filters.spaces > 1) {
        children.push({
          child: `${filters.spaces} Spaces`,
        })
      }
    } else {
      if (
        filters.minRateHistoric &&
        filters.maxRateHistoric &&
        filters.minRateHistoric !== filters.maxRateHistoric
      ) {
        children.push({
          child: `$${filters.minRateHistoric} - $${filters.maxRateHistoric} SF/year`,
          content: `Rate: $${filters.minRateHistoric} - $${filters.maxRateHistoric} SF/year`,
        })
      } else if (filters.minRateHistoric) {
        children.push({
          child: `$${filters.minRateHistoric} SF/year`,
          content: `Rate: $${filters.minRateHistoric} SF/year`,
        })
      }
      if (
        filters.minSfHistoric &&
        filters.maxSfHistoric &&
        fmtNumberAbbr(filters.minSfHistoric) !==
          fmtNumberAbbr(filters.maxSfHistoric)
      ) {
        children.push({
          child: `${fmtNumberAbbr(filters.minSfHistoric)} - ${fmtNumberAbbr(filters.maxSfHistoric)} SF`,
          content: `Available SF: ${filters.minSfHistoric.toLocaleString()} - ${filters.maxSfHistoric.toLocaleString()} SF`,
        })
      } else if (filters.minSfHistoric) {
        children.push({
          child: `${fmtNumberAbbr(filters.minSfHistoric)} SF`,
          content: `Available SF: ${filters.minSfHistoric.toLocaleString()} SF`,
        })
      }
    }
    if (user && filters.transactionCount && filters.transactionCount > 0) {
      children.push({
        child: `${filters.transactionCount} ${filters.transactionCount === 1 ? 'Transaction' : 'Transactions'}`,
        content: `Transactions: ${filters.transactionCount}`,
      })
    }
  }
  if (filters.buildingSf) {
    children.push({
      child: (
        <span className="inline-flex items-center gap-1">
          <LucideBuilding className="h-3.5 w-3.5 text-muted-foreground" />
          {`${fmtNumberAbbr(filters.buildingSf)} SF`}
        </span>
      ),
      content: `Building SF: ${filters.buildingSf.toLocaleString()} SF`,
    })
  }

  const lotSizeBadge = getLotSizeBadge(listing)
  if (lotSizeBadge) {
    children.push(lotSizeBadge)
  }

  if (listing.meta?.totalUnits) {
    children.push({
      child: `${listing.meta.totalUnits} Units`,
      content: `Total Units: ${listing.meta.totalUnits}`,
    })
  }

  // const dom = getDom(listing.activeDate || listing.created)

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      <Badge
        className={clsx(
          'text-base',
          listing.type === 'sale' && 'bg-[#0284c7] hover:bg-[#0284c7]',
          listing.type === 'lease' && 'bg-[#c026d3] hover:bg-[#c026d3]',
          listing.type === 'saleLease' && 'bg-[#4f46e5] hover:bg-[#4f46e5]'
        )}>
        {listing.status === 'active'
          ? TYPES[listing.type]
          : STATUSES[listing.status]}
      </Badge>
      {listing.isBusinessListing && (
        <Badge className="bg-[#a21caf] text-base hover:bg-[#a21caf]">
          Business
        </Badge>
      )}
      {listing.propertyTypes.map((value, index) => {
        return (
          <Badge
            key={index}
            className={clsx('text-base', getPropertyTypeClassName(value))}>
            <span>{value}</span>
          </Badge>
        )
      })}
      {/* {listing.type !== 'lease' && (
        <Tooltip.Provider delayDuration={200}>
          <Tooltip>
            <Tooltip.Trigger>
              <Badge variant="secondary" className="text-base">
                {`${dom} DOM`}
              </Badge>
            </Tooltip.Trigger>
            <Tooltip.Content side="top" className="text-base">
              {`Days on Market: ${dom}`}
            </Tooltip.Content>
          </Tooltip>
        </Tooltip.Provider>
      )} */}

      {children.map(({ child, content }, index) => (
        <Badge key={index} variant="secondary" className="text-base">
          {content ? (
            <Tooltip.Provider delayDuration={200}>
              <Tooltip>
                <Tooltip.Trigger>{child}</Tooltip.Trigger>
                <Tooltip.Content side="top" className="text-base">
                  {content}
                </Tooltip.Content>
              </Tooltip>
            </Tooltip.Provider>
          ) : (
            child
          )}
        </Badge>
      ))}
    </div>
  )
}

function formatSf(value: number) {
  return fmtNumberAbbr(Math.round(value * 100) / 100)
}

function formatAcres(value: number) {
  return fmtNumberAbbr(Math.round(value * 1000) / 1000)
}

function getFullListingMeta(listing: Listing | MapListingProjection) {
  const meta = listing.meta

  if (!meta || !('priceType' in meta)) {
    return undefined
  }

  return meta
}

function getLotSizeBadge(
  listing: Listing | MapListingProjection
): CardBadge | undefined {
  const filters = listing.filters
  const meta = getFullListingMeta(listing)
  const lotSizeUnit = meta?.lotSizeUnit ?? filters?.lotSizeUnit

  if (lotSizeUnit === 'sf') {
    const lotSizeSf = filters?.lotSizeSf?.[0]
    const min = meta?.lotSizeUnit === 'sf' ? meta.lotSizeMin : lotSizeSf?.min
    const max = meta?.lotSizeUnit === 'sf' ? meta.lotSizeMax : lotSizeSf?.max

    if (!min) {
      return undefined
    }

    if (max && formatSf(min) !== formatSf(max)) {
      return {
        child: `${formatSf(min)} - ${formatSf(max)} SF`,
        content: `Lot Size: ${min.toLocaleString()} - ${max.toLocaleString()} SF`,
      }
    }

    return {
      child: `${formatSf(min)} SF`,
      content: `Lot Size: ${min.toLocaleString()} SF`,
    }
  }

  const minAcres = meta?.lotSizeMin ?? filters?.minAcres
  const maxAcres = meta?.lotSizeMax ?? filters?.maxAcres

  if (minAcres && maxAcres && formatAcres(minAcres) !== formatAcres(maxAcres)) {
    return {
      child: `${formatAcres(minAcres)} - ${formatAcres(maxAcres)} ac`,
      content: `Lot Size: ${minAcres.toLocaleString()} - ${maxAcres.toLocaleString()} ac`,
    }
  } else if (minAcres) {
    return {
      child: `${formatAcres(minAcres)} ac`,
      content: `Lot Size: ${minAcres.toLocaleString()} ac`,
    }
  }
}

function CardCompany({
  company,
  brokers,
}: {
  company?: ModelCompany
  brokers: ((TransactionBroker & { meta?: Listing['brokers'][number]['meta'] }) | Listing['brokers'][number])[]
}) {
  const queryClient = useQueryClient()
  const data = queryClient.getQueryData(['active-companies']) as {
    companies: Company[] | undefined
  }
  const logo = useMemo(() => {
    if (!company) {
      return undefined
    }

    return data?.companies?.find((c) => company._id === c._id)?.meta?.logo
  }, [company, data])

  return (
    <div className="flex items-center gap-2 overflow-hidden px-4 pb-4">
      {brokers.length > 0 && (
        <span>
          <ListingPreviewBrokers className="p-0" brokers={brokers} />
        </span>
      )}
      {company && (
        <Link
          className="group flex items-center gap-2"
          to={`/companies/${company.slug}`}
          target="_blank"
          onClick={(e) => e.stopPropagation()}>
          {logo && (
            <img
              className="h-10 w-10 object-contain"
              alt={`${company.name} Logo`}
              src={logo.thumbUrl}
            />
          )}
          <span className="line-clamp-1 underline-offset-4 group-hover:underline">
            {company.name}
          </span>
        </Link>
      )}
    </div>
  )
}

type ListingCardBaseProps = {
  className?: string
  listing: Listing | MapListingProjection
  link?: string
  showCompany?: boolean
  onHide?: () => void
  onMouseOver?: () => void
  onMouseOut?: () => void
  onClick?: React.MouseEventHandler
  checked?: boolean
  onCheckChanged?: (value: boolean) => void
  lazyLoadImage?: boolean
}

export default function ListingCardBase({
  className,
  listing,
  link,
  showCompany = true,
  onHide,
  onMouseOver,
  onMouseOut,
  onClick,
  checked,
  onCheckChanged,
  lazyLoadImage = false,
}: ListingCardBaseProps) {
  const { isEmbed, openLinksInNewTab } = useEmbed()
  const user = useAtomValue(aUser)
  const showExternalLink = !isEmbed || openLinksInNewTab
  const transactionListingBrokers = useMemo(
    () => getLatestTransactionListingBrokers(listing),
    [listing]
  )
  const displayBrokers = useMemo(
    () =>
      mergeTransactionBrokersWithListingBrokerDetails(
        transactionListingBrokers,
        listing.brokers
      ),
    [transactionListingBrokers, listing.brokers]
  )
  const displayCompany =
    getSharedBrokerCompany(transactionListingBrokers) ?? listing.company

  return (
    <div
      className={cn(
        'relative h-full rounded-md bg-background shadow-md',
        onClick && 'cursor-pointer',
        className
      )}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}>
      <div className="absolute flex w-full justify-between gap-2 p-2">
        <div>
          {onCheckChanged && (
            <SelectionCheckbox
              checked={checked || false}
              onCheckChanged={onCheckChanged}
            />
          )}
        </div>

        <div className="flex gap-2">
          {user && onHide && (
            <Tooltip.Provider>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <Button
                    asChild
                    variant="ghost"
                    size="map-sm"
                    className="z-10 bg-white shadow-md ring-primary ring-offset-transparent hover:bg-white hover:outline-none hover:ring-1 hover:ring-offset-1"
                    onClick={onHide}>
                    <span>
                      <LucideEyeOff className="h-4 w-4" />
                    </span>
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content className="z-10" side="bottom">
                  Hide listing for this search
                </Tooltip.Content>
              </Tooltip>
            </Tooltip.Provider>
          )}

          {showExternalLink && (
            <Button
              asChild
              variant="ghost"
              size="map-sm"
              className="z-10 bg-white shadow-md ring-primary ring-offset-transparent hover:bg-white hover:outline-none hover:ring-1 hover:ring-offset-1">
              <Link
                to={link ? link : `/listings/${listing.slug}`}
                target="_blank">
                <LucideExternalLink className="h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </div>
      <div className={cn('flex h-full flex-col')} onClick={onClick}>
        <div className="relative aspect-video overflow-hidden rounded-t-md bg-muted">
          {(listing.images || []).length > 0 ? (
            <Image
              className="aspect-video w-full object-cover"
              src={listing.images![0].thumbUrl}
              lazy={lazyLoadImage}
            />
          ) : (
            <LucideImage className="absolute inset-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform text-foreground/75" />
          )}
          {getDom(listing.activeDate || listing.created) < 10 && (
            <Badge className="absolute bottom-2 right-2 bg-green-500 text-sm font-semibold hover:bg-green-500">
              NEW
            </Badge>
          )}
        </div>
        <div className="flex grow flex-col items-start px-4 pb-4 pt-3">
          <h3 className="mb-1 line-clamp-1 text-lg font-medium">
            <Link
              to={link ? link : `/listings/${listing.slug}`}
              target={
                !link || !isEmbed || openLinksInNewTab ? '_blank' : undefined
              }
              className="underline-offset-4 hover:underline"
              onClick={(e) => e.stopPropagation()}>
              {listing.name}
            </Link>
          </h3>
          <p className="whitespace-pre-line text-base font-light tracking-wider text-foreground">
            {fmtAddress(listing.property, {
              locationVisibility: listing.locationVisibility,
            })}
          </p>
          <CardBadges listing={listing} user={user} />
        </div>
        {showCompany && (
          <CardCompany company={displayCompany} brokers={displayBrokers} />
        )}
      </div>
    </div>
  )
}
